import cn from "classnames"
import React, { useEffect, useRef, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import videojs from "video.js"
import "video.js/dist/video-js.css"
import type Player from "video.js/dist/types/player"
import { defaultPatternByHoleCount, DIST_STEP_MM, SIZE_STEP_MM } from "../app/fixmethods/custom_holes"
import ControlPatterns from "./control_patterns"
import ClientData from "@/common/client_data"
import Scrollable from "@/common/components/scrollable"
import Slider from "@/common/components/slider"
import env from "@/common/env"
import { useFeatureIsOn } from "@/common/growthbook-react"
import { sizeUnit } from "@/common/locales"
import { isTablet } from "@/common/util"
import Icons from "@/images/signomatic/fixmethods"
import {
  validateHoleCount,
  validateHoleDistance,
  validateHoleSize,
  DEFAULT_COUNT,
  DEFAULT_SIZE_MM,
  DEFAULT_DISTANCE_MM,
  DEFAULT_DISTANCE_SCREW_MM,
  COUNT_MIN,
  COUNT_MAX,
  SIZE_MIN_MM,
  SIZE_MAX_MM,
  DIST_MIN_MM,
  DIST_MAX_MM,
} from "@/signomatic/app/fixmethods/custom_holes"
import { currentPresetSettings, optionForcedState } from "@/signomatic/app/sign_validator"
import { PATTERNS } from "@/signomatic/components/control_patterns"
import SelectedIndicator from "@/signomatic/components/selected_indicator"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import { ArrowRight, ChevronLeftLarge, RotateLeft } from "@/skylticons"

type Tab = "fixmethods" | "custom-holes"

interface SliderMinMax {
  min: number
  max: number
}

interface CustomHoleSliderRanges {
  count: SliderMinMax
  size: SliderMinMax
  distance: SliderMinMax
}

export const description = (type: string, signType: string, material: string): string => {
  if (type === "none") {
    return ""
  }

  const plastic =
    ["plastic_sign", "contour_cut", "tactile_sign"].includes(signType) ||
    (signType === "label_plates" && material === "regular")

  if (["self_adhesive_pad", "tape"].includes(type)) {
    if (plastic) {
      type += "_plastic"
    } else if (
      [
        "forex_sign",
        "solid_plastic_sign",
        "aluminium_sign",
        "label_plates",
        "brass_sign",
        "wood_sign",
        "ecoboard",
        "stainless_steel_sign",
      ].includes(signType)
    ) {
      type += "_alu"
    }
  }

  const labelplateBrass = signType === "label_plates" && material === "brass"

  if (type === "tape_and_screw") {
    if (labelplateBrass) {
      type += "_brass"
    }
  }

  if (type === "magnet" && signType === "magnetic_sign") {
    type = "magnet_magnetic"
  }

  if (isTablet()) {
    type += "_short"
  }

  return `fixmethod_toolbox.${type}`
}

export const reason = (type: string): string => {
  switch (type) {
    case "shape":
      return "toolbox.fix_method_disabled_shape"
    case "size":
      return "toolbox.fix_method_disabled_size"
    case "color":
      return "toolbox.fix_method_disabled_color"
    case "thickness":
      return "toolbox.fix_method_disabled_thickness"
    default:
      return ""
  }
}

const media = (signType: string, key: string): { video: string | null; image: string | null; text: string | null } => {
  const sign = ClientData.signs?.find(s => s.key === signType)
  const current = sign && sign.fixmethods ? sign.fixmethods.find(method => method.key === key) : null

  if (!current) {
    return { video: null, image: null, text: null }
  }

  current.text = ClientData.toolboxSnippet(key)

  return current
}

interface VideoProps {
  src: string
}

const Video: React.FC<VideoProps> = ({ src }) => {
  const videoRef = useRef(null)

  useEffect(() => {
    let player: Player
    if (videoRef.current) {
      player = videojs(videoRef.current, {
        controls: true,
        preload: "auto",
        fluid: true,
      })
    }

    return () => {
      if (player) {
        player.dispose()
      }
    }
  }, [])

  return (
    <div className="video-wrapper">
      <video ref={videoRef} className="video-js vjs-default-skin">
        <source src={src} type="video/mp4" />
      </video>
    </div>
  )
}

interface TopBarProps {
  backTo: Tab
  header: string
  setActiveTab: (backTo: Tab) => void
}

const TopBar: React.FC<TopBarProps> = ({ backTo, header, setActiveTab }) => {
  const { t } = useTranslation()

  return (
    <div className="ml-3 flex h-12 items-center">
      <button
        className="toolbox-back button button-subtle icon-left mb-0 px-2"
        onClick={() => {
          setActiveTab(backTo)
        }}
        type="button"
      >
        <ChevronLeftLarge className="!mr-1 h-6 w-6" />
        <span className="text-sm font-semibold">{t("toolbox.back")}</span>
      </button>
      <span className="absolute left-1/2 -translate-x-1/2 text-base font-semibold">{header}</span>
    </div>
  )
}

interface FixmethodDetailsProps {
  image?: string | null
  video?: string | null
  text?: string | null
}

const FixmethodDetails: React.FC<FixmethodDetailsProps> = ({ image, video, text }) => (
  <div className="described-details fixmethod">
    {image && <img src={image} alt="" />}
    {video && <Video key={video} src={video} />}
    {text && <div className="description" dangerouslySetInnerHTML={{ __html: text }} />}
  </div>
)

interface FixmethodProps {
  name: string
  description: string
  disabledReason: string
  shape: string
  signType: string
  material: string
  enabled: boolean
  active: boolean
  image: boolean
  video: boolean
  text: boolean
  update: () => void
  onReadMore: (e: React.MouseEvent) => void
  setActiveTab: (tab: Tab) => void
}

const Fixmethod = ({
  name,
  description,
  enabled,
  active,
  image,
  video,
  text,
  disabledReason,
  shape,
  signType,
  material,
  onReadMore,
  update,
  setActiveTab,
}: FixmethodProps) => {
  const { t } = useTranslation()
  const customHolesFlag = useFeatureIsOn("custom-holes-9377")
  const customHolesConditions =
    customHolesFlag &&
    ["screw", "tape_and_screw"].includes(name) &&
    shape === "rect" &&
    (signType === "label_plates" || material === "stainless") &&
    material !== "brass"

  return (
    <div className={cn("described", "selection", `fixmethod-${name}`, { disabled: !enabled, active })} onClick={update}>
      {!enabled && (
        <div className={cn("disabled-reason paragraph-2xs bold", disabledReason)}>{t(reason(disabledReason))}</div>
      )}

      <div className="contents">
        <div className="image">{Icons[name] ? React.createElement(Icons[name]) : ""}</div>
        <div className={cn("details", { center: !description })}>
          <h5 className="mb-0" dangerouslySetInnerHTML={{ __html: t(`fixmethod.${name}`) }} />
          <p className="paragraph-2xs mb-0">{description}</p>
          {description ? (
            <>
              {["pole", "pole_flanged"].includes(name) && (
                <p className="paragraph-2xs">
                  <br />
                  {env.locale == "en-GB" ? "(Ø 76 mm)" : `(Ø ${sizeUnit === "in" ? "2-2.5" : "50–65"} ${sizeUnit}) `}
                </p>
              )}
              {image || video || text ? (
                <div className={cn("info-row", { "flex-col !items-start": name === "custom_holes" })}>
                  <a onClick={onReadMore} className={cn({ video, image, text })}>
                    {video && t("toolbox.see_video")}
                    {image && t("toolbox.see_image")}
                    {text && t("misc.readmore")}
                    ...
                  </a>
                  <div className={cn("flex", { "w-full justify-between": name === "custom_holes" })}>
                    <p className="paragraph-xs mb-0">
                      {name === "custom_holes" && (
                        <button
                          className="button button-small button-subtle link-button icon-right mb-0 mt-1 text-ink-100"
                          type="button"
                          onClick={() => setActiveTab("custom-holes")}
                        >
                          {t("toolbox.hole_settings")}
                          <ArrowRight />
                        </button>
                      )}
                    </p>
                    <SelectedIndicator active={active} />
                  </div>
                </div>
              ) : (
                <div
                  className={cn("info-row", {
                    right: !customHolesConditions,
                  })}
                >
                  {customHolesConditions && (
                    <button
                      className="button button-small button-subtle link-button icon-right mb-0 mt-1 text-ink-100"
                      type="button"
                      onClick={() => setActiveTab("custom-holes")}
                    >
                      {t("toolbox.hole_settings")}
                      <ArrowRight />
                    </button>
                  )}
                  <SelectedIndicator active={active} />
                </div>
              )}
            </>
          ) : (
            <SelectedIndicator active={active} />
          )}
        </div>
      </div>
    </div>
  )
}

interface FixmethodToolboxStateProps {
  data: SignData
  signType: string
  signState: SignState
  subToolbox: string
}

interface FixmethodToolboxDispatchProps {
  setSubToolbox: typeof actions.setSubToolbox
}

type FixmethodToolboxProps = FixmethodToolboxStateProps & FixmethodToolboxDispatchProps & ToolboxStripItemProps

const FixmethodToolbox: React.FC<FixmethodToolboxProps> = ({
  sign,
  signType,
  signState,
  data,
  subToolbox,
  setSubToolbox,
}) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<Tab>("fixmethods")
  const currentMedia = subToolbox ? media(signType, subToolbox) : null
  const presetMatch = currentPresetSettings(signState, data)
  const adminFixmethods = ClientData.admin ? data.fixmethods.filter(fixmethod => fixmethod.filters?.admin) : []
  const ballChainFlag = useFeatureIsOn("ball-chain-9354")
  const customHolesFlag = useFeatureIsOn("custom-holes-9377")
  const fixmethodDefaultDist = signState.fixmethod === "custom_holes" ? DEFAULT_DISTANCE_MM : DEFAULT_DISTANCE_SCREW_MM
  const customHolesEnabled =
    customHolesFlag &&
    ["custom_holes", "screw", "tape_and_screw"].includes(signState.fixmethod || "") &&
    signState.shape === "rect" &&
    (signType === "label_plates" || signState.material === "stainless") &&
    signState.material !== "brass"
  const [customHoleRanges, setCustomHoleRanges] = useState<CustomHoleSliderRanges>({
    count: { min: COUNT_MIN, max: COUNT_MAX },
    size: { min: SIZE_MIN_MM, max: SIZE_MAX_MM },
    distance: { min: DIST_MIN_MM, max: DIST_MAX_MM },
  })

  const patternValidity = useMemo(() => {
    if (!customHolesEnabled) {
      return {}
    }

    const count = signState.customHoleCount || DEFAULT_COUNT
    const validity: Record<string, boolean> = {}

    PATTERNS[count].forEach(pattern => {
      validity[pattern] = validateHoleCount(signState, count, pattern).limitExceeded
    })

    return validity
  }, [signState, customHolesEnabled])

  useEffect(() => {
    if (!signState || !customHolesEnabled) return

    const countRes = validateHoleCount(signState, signState.customHoleCount || DEFAULT_COUNT)
    const sizeRes = validateHoleSize(signState, signState.customHoleSizeMm || DEFAULT_SIZE_MM)
    const distanceRes = validateHoleDistance(signState, signState.customHoleDistanceMm || fixmethodDefaultDist)

    setCustomHoleRanges({
      count: { min: COUNT_MIN, max: countRes.max },
      size: { min: SIZE_MIN_MM, max: sizeRes.max },
      distance: { min: distanceRes.min, max: distanceRes.max },
    })
  }, [signState, customHolesEnabled, fixmethodDefaultDist])

  const handleHoleCountChange = (newCount: number) => {
    const countRes = validateHoleCount(signState, newCount)
    const tempState = { ...signState, customHoleCount: countRes.value }

    const sizeRes = validateHoleSize(tempState, tempState.customHoleSizeMm || DEFAULT_SIZE_MM)
    const distanceRes = validateHoleDistance(tempState, tempState.customHoleDistanceMm || DEFAULT_DISTANCE_MM)
    const countUpdated = countRes.value !== signState.customHoleCount

    sign.updateState({
      customHoleCount: countRes.value,
      customHoleControlPattern: countUpdated
        ? defaultPatternByHoleCount[countRes.value]
        : tempState.customHoleControlPattern,
    })

    setCustomHoleRanges({
      count: { min: COUNT_MIN, max: countRes.max },
      size: { min: SIZE_MIN_MM, max: sizeRes.max },
      distance: { min: distanceRes.min, max: distanceRes.max },
    })
  }

  const handleHoleSizeChange = (newSize: number) => {
    const sizeRes = validateHoleSize(signState, newSize)
    const tempState = { ...signState, customHoleSizeMm: sizeRes.value }

    const countRes = validateHoleCount(tempState, tempState.customHoleCount || DEFAULT_COUNT)
    const distanceRes = validateHoleDistance(tempState, tempState.customHoleDistanceMm || DEFAULT_DISTANCE_MM)

    sign.updateState({
      customHoleSizeMm: sizeRes.value,
    })

    setCustomHoleRanges({
      count: { min: COUNT_MIN, max: countRes.max },
      size: { min: SIZE_MIN_MM, max: sizeRes.max },
      distance: { min: distanceRes.min, max: distanceRes.max },
    })
  }

  const handleHoleDistanceChange = (newDistance: number) => {
    const distanceRes = validateHoleDistance(signState, newDistance)
    const tempState = { ...signState, customHoleDistanceMm: distanceRes.value }

    const countRes = validateHoleCount(tempState, tempState.customHoleCount || DEFAULT_COUNT)
    const sizeRes = validateHoleSize(tempState, tempState.customHoleSizeMm || DEFAULT_SIZE_MM)

    sign.updateState({ customHoleDistanceMm: distanceRes.value })

    setCustomHoleRanges({
      count: { min: COUNT_MIN, max: countRes.max },
      size: { min: SIZE_MIN_MM, max: sizeRes.max },
      distance: { min: distanceRes.min, max: distanceRes.max },
    })
  }

  function customHolesTab() {
    if (!signState) {
      return
    }

    return (
      <div className="custom-holes-tab h-full py-4">
        <div className="custom-holes h-[calc(100%-40px)]">
          <Scrollable>
            <div className="pl-4 pr-2">
              <Slider
                inputId="hole_count"
                inputName="hole_count_range"
                label={t("toolbox.hole_count")}
                className="mb-4"
                min={COUNT_MIN}
                max={COUNT_MAX}
                value={signState.customHoleCount || DEFAULT_COUNT}
                hideRangeLabels
                inputRight
                onChange={value => {
                  handleHoleCountChange(value)
                }}
                customRange={[1, 2, 4]}
                enabledMin={customHoleRanges.count.min}
                enabledMax={customHoleRanges.count.max}
              />
            </div>

            <div className="control-patterns mb-4 pl-4 pr-2">
              <ControlPatterns
                customHoleCount={signState.customHoleCount || DEFAULT_COUNT}
                sign={sign}
                validity={patternValidity}
              />
            </div>

            {signState.fixmethod === "custom_holes" && (
              <div className="pl-4 pr-2">
                <Slider
                  inputId="hole_size"
                  inputName="hole_size_range"
                  label={t("toolbox.hole_size")}
                  className="mb-4"
                  min={SIZE_MIN_MM}
                  max={SIZE_MAX_MM}
                  step={SIZE_STEP_MM}
                  value={signState.customHoleSizeMm || DEFAULT_SIZE_MM}
                  hideRangeLabels
                  inputRight
                  onChange={value => {
                    handleHoleSizeChange(value)
                  }}
                  enabledMin={customHoleRanges.size.min}
                  enabledMax={customHoleRanges.size.max}
                />
              </div>
            )}

            <div className="pl-4 pr-2">
              <Slider
                inputId="hole_distance"
                inputName="hole_distance_range"
                label={t("toolbox.hole_distance")}
                tooltip={t("toolbox.hole_tooltip")}
                className="mb-4"
                min={DIST_MIN_MM}
                max={DIST_MAX_MM}
                step={DIST_STEP_MM}
                value={signState.customHoleDistanceMm || fixmethodDefaultDist}
                hideRangeLabels
                inputRight
                onChange={value => {
                  handleHoleDistanceChange(value)
                }}
                enabledMin={customHoleRanges.distance.min}
                enabledMax={customHoleRanges.distance.max}
              />
            </div>

            <div className="pl-4">
              <button
                className="button button-secondary button-small icon-left mt-2"
                type="button"
                onClick={() => {
                  sign.updateState({
                    customHoleCount: DEFAULT_COUNT,
                    customHoleSizeMm: DEFAULT_SIZE_MM,
                    customHoleDistanceMm: fixmethodDefaultDist,
                    customHoleControlPattern: "center_x",
                  })
                }}
              >
                <RotateLeft />
                {t("toolbox.reset")}
              </button>
            </div>
          </Scrollable>
        </div>
      </div>
    )
  }

  function fixmethodsTab() {
    if (!signState) {
      return
    }

    return (
      <>
        <Scrollable scrollToActive scrollActiveToolboxDelay>
          {data.fixmethods.map(fixmethod => {
            const active = signState.fixmethod === fixmethod.name
            const { image, video, text } = media(signType, fixmethod.name)
            const hasMedia = !!image || !!video || !!text

            if (fixmethod.filters) {
              if (fixmethod.filters.admin) {
                return null
              }

              if ((fixmethod.filters.hidden && !active) || (fixmethod.filters.legacy && !active)) {
                return null
              }

              if (fixmethod.filters.hide_shapes?.includes(signState.shape)) {
                return null
              }
            }

            if (fixmethod.disabled_reason && fixmethod.disabled_reason.includes("locale")) {
              return null
            }

            if (presetMatch && presetMatch.fixmethods) {
              if (!presetMatch.fixmethods.map(method => method.name).includes(fixmethod.name)) {
                return null
              }
            }

            if (fixmethod.name === "custom_holes" && !customHolesFlag) {
              return null
            }

            if (fixmethod.name === "ball_chain" && !ballChainFlag) {
              return null
            }

            let enabled = fixmethod.enabled
            let forcedState: Partial<SignState> = {}

            if (
              !enabled &&
              fixmethod.forced_settings &&
              fixmethod.disabled_reason?.every(r => fixmethod.forced_settings?.includes(r))
            ) {
              enabled = true
              forcedState = optionForcedState(fixmethod, signState, fixmethod.forced_settings)
            }

            if (fixmethod.name === "table_stand") {
              forcedState.shape = "rect"
            }

            return (
              <Fixmethod
                key={fixmethod.name}
                name={fixmethod.name}
                description={t(description(fixmethod.name, signType, signState.material))}
                active={active}
                enabled={enabled || false}
                disabledReason={(fixmethod.disabled_reason && fixmethod.disabled_reason[0]) || ""}
                update={() => {
                  if (!enabled) {
                    return
                  }

                  sign.updateState({ fixmethod: fixmethod.name, ...forcedState })

                  if (!hasMedia) {
                    setSubToolbox(null)
                  } else if (subToolbox && subToolbox !== fixmethod.name) {
                    setSubToolbox(fixmethod.name)
                  }

                  if (fixmethod.name === "custom_holes") {
                    setActiveTab("custom-holes")
                  }
                }}
                image={!!image}
                video={!!video}
                text={!!text}
                onReadMore={e => {
                  e.stopPropagation()
                  e.preventDefault()

                  setSubToolbox(fixmethod.name)
                }}
                setActiveTab={setActiveTab}
                shape={signState.shape}
                signType={signType}
                material={signState.material}
              />
            )
          })}
          {adminFixmethods.length > 0 && (
            <>
              <div className="px-4 py-2">
                <div className="border-t-4 border-t-ink-85">SignAdmin users only</div>
              </div>
              {adminFixmethods.map(adminFixmethod => {
                const active = signState.fixmethod === adminFixmethod.name
                const { image, video, text } = media(signType, adminFixmethod.name)
                const hasMedia = !!image || !!video || !!text

                if (adminFixmethod.filters) {
                  if ((adminFixmethod.filters.hidden && !active) || (adminFixmethod.filters.legacy && !active)) {
                    return null
                  }

                  if (adminFixmethod.filters.hide_shapes?.includes(signState.shape)) {
                    return null
                  }
                }

                if (adminFixmethod.disabled_reason && adminFixmethod.disabled_reason.includes("locale")) {
                  return null
                }

                if (presetMatch && presetMatch.fixmethods) {
                  if (!presetMatch.fixmethods.map(method => method.name).includes(adminFixmethod.name)) {
                    return null
                  }
                }

                let enabled = adminFixmethod.enabled
                let forcedState: Partial<SignState> = {}

                if (
                  !enabled &&
                  adminFixmethod.forced_settings &&
                  adminFixmethod.disabled_reason?.every(r => adminFixmethod.forced_settings?.includes(r))
                ) {
                  enabled = true
                  forcedState = optionForcedState(adminFixmethod, signState, adminFixmethod.forced_settings)
                }

                if (adminFixmethod.name === "table_stand") {
                  forcedState.shape = "rect"
                }

                return (
                  <Fixmethod
                    key={adminFixmethod.name}
                    name={adminFixmethod.name}
                    description={t(description(adminFixmethod.name, signType, signState.material))}
                    active={active}
                    enabled={enabled || false}
                    disabledReason={(adminFixmethod.disabled_reason && adminFixmethod.disabled_reason[0]) || ""}
                    update={() => {
                      if (!enabled) {
                        return
                      }

                      sign.updateState({ fixmethod: adminFixmethod.name, ...forcedState })

                      if (!hasMedia) {
                        setSubToolbox(null)
                      } else if (subToolbox && subToolbox !== adminFixmethod.name) {
                        setSubToolbox(adminFixmethod.name)
                      }
                    }}
                    image={!!image}
                    video={!!video}
                    text={!!text}
                    onReadMore={e => {
                      e.stopPropagation()
                      e.preventDefault()

                      setSubToolbox(adminFixmethod.name)
                    }}
                    setActiveTab={setActiveTab}
                    shape={signState.shape}
                    signType={signType}
                    material={signState.material}
                  />
                )
              })}
            </>
          )}
        </Scrollable>
      </>
    )
  }

  const tabComponents = () => {
    if (activeTab === "custom-holes") {
      return customHolesTab()
    }

    return fixmethodsTab()
  }

  return (
    <Toolbox tKey="fixmethod" hideHeaderBar={activeTab == "custom-holes"}>
      <>
        {activeTab == "custom-holes" && (
          <TopBar backTo="fixmethods" header={t("fixmethod.custom_holes")} setActiveTab={setActiveTab} />
        )}

        {tabComponents()}
      </>
      {currentMedia &&
        subToolbox &&
        (isTablet() ? (
          <Scrollable>
            <FixmethodDetails image={currentMedia.image} video={currentMedia.video} text={currentMedia.text} />
          </Scrollable>
        ) : (
          <FixmethodDetails image={currentMedia.image} video={currentMedia.video} text={currentMedia.text} />
        ))}
    </Toolbox>
  )
}

export default connect(
  ({ signState, currentSignData, subToolbox, signType }: State) => ({
    signState,
    data: currentSignData,
    subToolbox,
    signType,
  }),
  {
    setSubToolbox: actions.setSubToolbox,
  }
)(FixmethodToolbox)
